import { ENDPOINTS, get, put } from '@/api/mainApi';
import Vue from 'vue';
import { Snackbar } from '@/interfaces/presentation/snackbar';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { PaginatedResponse } from '@/interfaces/paginatedResponse';
import { Depot, ProtectedIdentityResponse } from '@/interfaces/depot/depot';

export class DepotRepository {
    private getDepotsCancelToken: CancelTokenSource;


    constructor(private vueInstance: Vue) {
      this.getDepotsCancelToken = axios.CancelToken.source()
    }

    public async getDepots(
      statuses: string,
      page: number,
      limit: number,
      excludeToBeArchived: boolean | null,
      onlySignatureMissing: boolean | null,
      onlyFilesMissing: boolean | null,
      protectedIdentity: boolean | null,
      sortValue: string,
      sortOrder: string
    ): Promise<PaginatedResponse | Array<Depot>> {
      this.getDepotsCancelToken?.cancel("Superseeded by new request")
      this.getDepotsCancelToken = axios.CancelToken.source()

      try {
        return await get(`${ENDPOINTS.DEPOTS}`, {
          statuses: statuses,
          page: page,
          limit: limit,
          excludeToBeArchived: excludeToBeArchived,
          onlySignatureMissing: onlySignatureMissing,
          onlyFilesMissing: onlyFilesMissing,
          protectedIdentity: protectedIdentity,
          sortValue: sortValue,
          sortOrder: sortOrder
          
        }, this.getDepotsCancelToken.token);
      } catch (error) {
        this.handleError(error, `Misslyckades med att hämta depåer: ${error.data.msg || 'Okänt fel'}`);
        return Promise.reject(error);
      }
    }

    public async updateDepotName(depotId: number, depotName: string): Promise<string> {
        try {
            const response = await put(`${ENDPOINTS.DEPOTS}/${depotId}/${ENDPOINTS.NAME}/${depotName}`, {});
            this.setSnackbar('Uppdaterade namnet på depån', 'success');
            return response;
        } catch (error) {
            this.handleError(error, `Misslyckades med att uppdatera depånamnet: ${error.data.msg || 'Okänt fel'}`);
            return Promise.reject(error);
        }
    }

    public async isRecordedAsProtectedIdentity(depotId: number): Promise<ProtectedIdentityResponse> {
      try {
        const response = await get(`${ENDPOINTS.DEPOTS}/${depotId}/${ENDPOINTS.PROTECTED_IDENTITY}`, {})
        return response
      } catch (error) {
        this.handleError(error, `Kunde inte avgöra om depå har skyddad identitet: ${error.data.msg || 'Okänt fel'}`)
        return Promise.reject(error)
      }
    }

    public async getKeepsakesByStoragePlaceId(storagePlaceId: number, pageIndex: number, limit: number): Promise<PaginatedResponse> {
      try {
        const response = await get(`${ENDPOINTS.KEEPSAKES}/storageplace/${storagePlaceId}`, {
          pageIndex: pageIndex,
          limit: limit
        })
        return response
      } catch (error) {
        this.handleError(error, `Kunde inte hämta bevakningar för förvaringsplats: ${error.data.msg || 'Okänt fel'}`)
        return Promise.reject(error)
      }
    }

    private setSnackbar(message: string, color: 'error' | 'success') {
        const snackbar: Snackbar = {
            active: true,
            color: color,
            text: message,
            timeout: 10000,
            action: null,
        };
        this.vueInstance.$store.commit('setSnackbar', snackbar);
    }

    private handleError(error: AxiosError, errorMessage: string) {
        if (error.response?.status === 403) {
            this.setSnackbar('Du saknar rättigheter för att utföra den begärda handlingen', 'error');
        } else {
            this.setSnackbar(errorMessage, 'error');
        }
    }
}
